import Pending from '@crehana/compass.icons.fill/Pending';
import AlertCircle from '@crehana/compass.icons.outline/AlertCircle';
import CheckmarkCircle from '@crehana/compass.icons.outline/CheckmarkCircle';
import Eye from '@crehana/compass.icons.outline/Eye';
import Info from '@crehana/compass.icons.outline/Info';
import type { Maybe } from '@crehana/ts-types';
import { format, startOfDay } from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';

import type { PidModelLabels } from '@/__generated__/graphql/types';
import { MENU_LABEL, type TMenuLabelCode } from '@/shared/i18n';

import { STATE_ACTIVITY } from './constants';
import type { ModuleInfo } from './types';

export const getStateActivity = (state: STATE_ACTIVITY) => {
  if (state === STATE_ACTIVITY.IN_PROGRESS) {
    return {
      stateActivity: STATE_ACTIVITY.IN_PROGRESS_BY_LEADER,
      classState: '!tw-bg-on-hold-100 !tw-text-on-hold-400',
      flagTitle: true,
      icon: Eye,
    };
  }

  if (
    state === STATE_ACTIVITY.PENDING ||
    state === STATE_ACTIVITY.PENDING_BY_BOSS
  ) {
    return {
      stateActivity: STATE_ACTIVITY.PENDING_APPROVAL,
      classText: 'tw-text-neutral-light-700',
      classState: '!tw-bg-neutral-light-300 !tw-text-neutral-light-700',
      icon: AlertCircle,
    };
  }

  if (state === STATE_ACTIVITY.REJECTED) {
    return {
      stateActivity: STATE_ACTIVITY.REJECTED,
      classText: 'tw-text-alert-500',
      classState: '!tw-bg-alert-100 !tw-text-alert-500',
      icon: Info,
    };
  }

  if (state === STATE_ACTIVITY.FINISHED) {
    return {
      stateActivity: STATE_ACTIVITY.EVALUATED,
      classState: '!tw-bg-success-100 !tw-text-success-500',
      flagTitle: true,
      icon: CheckmarkCircle,
    };
  }

  if (state === STATE_ACTIVITY.APPROVED) {
    return {
      stateActivity: STATE_ACTIVITY.UNSTARTED,
      classText: 'tw-text-neutral-light-800',
      classState: '!tw-bg-neutral-light-500 !tw-text-neutral-light-100',
      flagTitle: true,
      icon: Pending,
    };
  }

  return {
    stateActivity: STATE_ACTIVITY.UNSTARTED,
    classState: '!tw-bg-neutral-light-500 !tw-text-neutral-light-100',
    classText: 'tw-text-neutral-light-800',
    flagTitle: true,
    icon: Pending,
  };
};

export const formatDate = (date: Date, languageUser: string, fmt = 'd MMM') => {
  let locale;

  switch (languageUser) {
    case 'en':
      locale = enUS;
      break;
    case 'pt':
      locale = ptBR;
      break;
    default:
      locale = es;
  }

  return format(date, fmt, { locale });
};

export const getModuleName = (model: Maybe<PidModelLabels> | undefined) => {
  const modelInfo: { [key: string]: ModuleInfo } = {
    ten: { name: model?.ten || '', percentage: 10 },
    twenty: { name: model?.twenty || '', percentage: 20 },
    seventy: { name: model?.seventy || '', percentage: 70 },
  };

  if (model?.ten) {
    return modelInfo.ten;
  }
  if (model?.twenty) {
    return modelInfo.twenty;
  }
  if (model?.seventy) {
    return modelInfo.seventy;
  }

  return undefined;
};

export const formatDateUTC = (date: Date) => {
  // Convertir la fecha a UTC
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

  return utcDate;
};

/**
 * Estado del rango de fechas del proceso PID respecto a hoy.
 * - NOT_STARTED: hoy es anterior a init_date_process (proceso aún no comienza)
 * - ACTIVE: hoy está dentro del rango [init_date_process, finish_date_process]
 * - FINISHED: hoy es posterior a finish_date_process
 */
type TProcessDateStatus = 'NOT_STARTED' | 'ACTIVE' | 'FINISHED';

export const getProcessDateStatus = (
  initDate?: string | null,
  finishDate?: string | null,
): TProcessDateStatus | undefined => {
  if (!initDate || !finishDate) {
    return undefined;
  }
  const today = startOfDay(new Date());
  if (today < startOfDay(new Date(initDate))) {
    return 'NOT_STARTED';
  }
  if (today > startOfDay(new Date(finishDate))) {
    return 'FINISHED';
  }
  return 'ACTIVE';
};

export const isProcessActive = (status?: TProcessDateStatus) =>
  status === 'ACTIVE';
export const isProcessNotStarted = (status?: TProcessDateStatus) =>
  status === 'NOT_STARTED';

/**
 * RN-006: `allow_max_act_comp_act` is a flag derived by the backend from the
 * limit itself (`allowmaxactcomptact = maxactcomp is not None`), so
 * number_activities=null together with allow_max_act_comp_act=false is the
 * "no limit" state. Any other combination means a limit is configured.
 */
export const hasActivitiesLimit = (
  allowMaxActCompAct?: boolean,
  numberActivities?: Maybe<number>,
) => !(numberActivities == null && allowMaxActCompAct === false);

/**
 * Without a configured limit the collaborator can always add activities;
 * otherwise the current count must stay below number_activities.
 */
export const canCreateMoreActivities = (
  currentCount: number,
  allowMaxActCompAct?: boolean,
  numberActivities?: Maybe<number>,
) => {
  if (!hasActivitiesLimit(allowMaxActCompAct, numberActivities)) {
    return true;
  }

  return numberActivities != null && currentCount < Math.abs(numberActivities);
};

/**
 * PID URL segments that map to a level of the centralized menu.
 *
 * The detail and the tracking breadcrumbs do not declare their levels. They
 * derive them from the `pathname`, split into segments. To make those levels
 * show the menu wording — which each company can personalize — the code must
 * know which segment is a menu module and which one is a leaf of the screen.
 * This map holds that answer. Read it through `resolvePidSegmentLabel`.
 *
 * Segments that are NOT here (`activity`, `user_activities_detail`) are leaves
 * that the menu does not know. They keep the translation of the screen.
 */
const PID_MENU_CODE_BY_URL_SEGMENT: Record<string, TMenuLabelCode> = {
  performance: MENU_LABEL.performance,
  pid: MENU_LABEL.pid,
};

/**
 * Resolves the label of one breadcrumb level derived from a PID URL segment.
 *
 * A segment in `PID_MENU_CODE_BY_URL_SEGMENT` takes the menu wording, with the
 * screen translation as fallback. Any other segment keeps the screen
 * translation. Both headers call this function, so the fallback rule stays
 * the same in the two breadcrumbs.
 */
export const resolvePidSegmentLabel = (
  segment: string,
  label: string,
  menuLabel: (code: TMenuLabelCode, fallback?: string) => string,
) => {
  const menuCode = PID_MENU_CODE_BY_URL_SEGMENT[segment];

  if (!menuCode) {
    return label;
  }

  return menuLabel(menuCode, label);
};
